import { useEffect, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import { CgEye, CgTrash } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import { openReport } from "../../utils/orderReports";
import DeleteConfirmModal from "../Commaon/DeleteConfirmModal";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { buildInvoiceHtml, getInvoiceLogoDataUrl } from "../../utils/Inovoice";
import { formatDate } from "../../utils/date";
import ListHeader from "../Commaon/ListHeader";
import ListPagination from "../Commaon/ListPagination";

const formatOrderId = (order, index) => {
  const source = order.orderDisplayId || order.orderId || order.orderNumber;
  if (source && String(source).length <= 4) return String(source).padStart(4, "0");
  return String(1001 + index).padStart(4, "0");
};
const getCustomerName = (order) =>
  order.customerName || order.members?.[0]?.fullName || order.members?.fullName || "-";
const getCustomerMobile = (order) =>
  order.customerMobileNumber || order.userDetails?.mobileNumber || "-";
const getLabTechnicianName = (order) => order.labTechnician?.name || "Not Assigned";
const getLabTechnicianNumber = (order) => order.labTechnician?.number || "Not Assigned";
const statusClass = (value) => {
  if (value === "Completed") return "bg-success";
  if (value === "Processing") return "bg-info text-dark";
  if (value === "COD") return "bg-primary";
  if (value === "Pending") return "bg-warning text-dark";
  if (value === "Cancelled" || value === "Failed") return "bg-danger";
  return "bg-secondary";
};

// Normalises order members (B3 contract) with their report-able items.
const getOrderMembers = (order) =>
  (Array.isArray(order?.members) ? order.members : [])
    .map((member) => ({
      orderMemberId: String(member.orderMemberId || ""),
      fullName: member.fullName || "Member",
      relation: member.relation || "",
      hardCopy: Boolean(member.hardCopy),
      items: (Array.isArray(member.items) ? member.items : [])
        .map((item) => ({
          orderItemId: String(item.orderItemId || ""),
          title: item.title || "-",
          type: item.type || "",
          reportId: item.reportId ? String(item.reportId) : null,
        }))
        .filter((item) => item.orderItemId),
    }))
    .filter((member) => member.orderMemberId);

const getOrderReports = (order) => (Array.isArray(order?.reports) ? order.reports.filter(Boolean) : []);

const formatMemberLabel = (member) =>
  member.relation ? `${member.fullName} (${member.relation})` : member.fullName;

const MemberOption = ({ member }) => (
  <span>
    {formatMemberLabel(member)}
    {member.hardCopy && <span className="badge bg-success ms-2 fw-normal">Hard copy</span>}
  </span>
);

const getReportFileName = (report) => report?.originalFileName || "Report";

const isPackageItem = (item) => String(item.type || "").toLowerCase() === "package";

const downloadInvoice = async (order) => {
  const printWindow = window.open("", "_blank", "width=900,height=1200");
  if (!printWindow) return;

  printWindow.document.write("<p style=\"font-family:Arial,sans-serif;padding:24px\">Preparing invoice…</p>");
  printWindow.document.close();

  const logoUrl = await getInvoiceLogoDataUrl();
  printWindow.document.open();
  printWindow.document.write(buildInvoiceHtml(order, logoUrl));
  printWindow.document.close();

  const printInvoice = () => {
    printWindow.focus();
    printWindow.onafterprint = () => printWindow.close();
    printWindow.print();
  };

  if (printWindow.document.readyState === "complete") {
    setTimeout(printInvoice, 250);
  } else {
    printWindow.onload = () => setTimeout(printInvoice, 250);
  }
};

const OrdersList = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [reportModal, setReportModal] = useState(false);
  const [reportOrderId, setReportOrderId] = useState("");
  const [reportFile, setReportFile] = useState(null);
  const [reportMembers, setReportMembers] = useState([]);
  const [existingReports, setExistingReports] = useState([]);
  const [reportToRemove, setReportToRemove] = useState(null);
  const [reportTarget, setReportTarget] = useState("");
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [reportConflicts, setReportConflicts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteIds, setDeleteIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/orders?page=${pageNo}&limit=${limit}`, {
        method: "GET",
      });
      if (response?.data?.s === 200) {
        setOrders(response.data.r || []);
        setFilteredOrders(response.data.r || []);
        setTotalRecords(response.data.tr || 0);
      }
    } catch (error) {
      setSnack({ notification: { message: "Failed to load orders", type: "error" } });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, pageNo]);

  useEffect(() => {
    const search = query.trim().toLowerCase();
    if (!search) {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(
        orders.filter((order, index) =>
          [
            formatOrderId(order, (pageNo - 1) * limit + index),
            order.orderNumber,
            getCustomerName(order),
            getCustomerMobile(order),
            order.paymentStatus,
            order.orderStatus,
            getLabTechnicianName(order),
            getLabTechnicianNumber(order),
          ]
            .join(" ")
            .toLowerCase()
            .includes(search)
        )
      );
    }
  }, [query, orders, pageNo, limit]);

  const totalPages = Math.ceil((totalRecords || filteredOrders.length) / limit) || 1;
  const orderOffset = (pageNo - 1) * limit;
  const visibleIds = filteredOrders.map((order) => order._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const openDeleteModal = (ids) => {
    if (!ids.length) {
      setSnack({ notification: { message: "Select at least one order", type: "error" } });
      return;
    }
    setDeleteIds(ids);
    setDeleteModal(true);
  };

  const deleteOrders = async () => {
    try {
      setLoading(true);
      const response = await AuthInterceptor("/admin/orders", {
        method: "DELETE",
        body: JSON.stringify({ ids: deleteIds }),
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        setSnack({
          notification: {
            message: response?.data?.m || "Orders deleted successfully",
            type: "success",
          },
        });
        setSelectedIds((prev) => prev.filter((id) => !deleteIds.includes(id)));
        setDeleteModal(false);
        fetchOrders();
      } else {
        setSnack({
          notification: { message: response?.data?.m || "Error deleting orders", type: "error" },
        });
      }
    } catch (error) {
      setSnack({ notification: { message: "Error deleting orders", type: "error" } });
    } finally {
      setLoading(false);
      setDeleteIds([]);
    }
  };

  // Selecting a member pre-ticks their test/package when they have exactly one.
  const selectReportMember = (orderMemberId, members = reportMembers) => {
    const member = members.find((item) => item.orderMemberId === orderMemberId);
    const items = member?.items || [];
    setReportTarget(orderMemberId || "");
    setSelectedItemIds(items.length === 1 ? [items[0].orderItemId] : []);
    setReportConflicts([]);
  };

  // Permanently removes an uploaded report (record + stored file); its items return to pending.
  const removeUploadedReport = async () => {
    if (!reportToRemove?._id) return;
    const removedId = String(reportToRemove._id);
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/order/report/${removedId}`, { method: "DELETE" });
      if (response?.data?.s === 200) {
        setExistingReports((prev) => prev.filter((report) => String(report._id) !== removedId));
        setReportMembers((prev) =>
          prev.map((member) => ({
            ...member,
            items: member.items.map((item) => (item.reportId === removedId ? { ...item, reportId: null } : item)),
          }))
        );
        setReportConflicts([]);
        setSnack({ notification: { message: response.data.m || "Report removed successfully", type: "success" } });
        fetchOrders();
      } else {
        setSnack({ notification: { message: response?.data?.m || "Failed to remove report", type: "error" } });
      }
    } catch (error) {
      setSnack({ notification: { message: "Failed to remove report", type: "error" } });
    } finally {
      setLoading(false);
      setReportToRemove(null);
    }
  };

  const openReportUpload = (order) => {
    const members = getOrderMembers(order);
    setReportOrderId(order._id);
    setReportFile(null);
    setReportMembers(members);
    setExistingReports(getOrderReports(order));
    selectReportMember(members.length === 1 ? members[0].orderMemberId : "", members);
    setReportModal(true);
  };

  const closeReportModal = () => {
    setReportModal(false);
    setReportConflicts([]);
  };

  const isMultiMemberOrder = reportMembers.length > 1;
  const selectedMember = reportMembers.find((member) => member.orderMemberId === reportTarget);
  const findReport = (reportId) => existingReports.find((report) => String(report._id) === String(reportId));

  const toggleReportItem = (orderItemId) => {
    setReportConflicts([]);
    setSelectedItemIds((prev) =>
      prev.includes(orderItemId) ? prev.filter((id) => id !== orderItemId) : [...prev, orderItemId]
    );
  };

  const uploadReport = async (event, replace = false) => {
    event?.preventDefault();
    if (!reportOrderId || !reportTarget) {
      setSnack({ notification: { message: "Please select a member for this report", type: "error" } });
      return;
    }
    if (selectedItemIds.length === 0) {
      setSnack({ notification: { message: "Select at least one test or package", type: "error" } });
      return;
    }
    if (!reportFile) {
      setSnack({ notification: { message: "Please choose a report file", type: "error" } });
      return;
    }
    const formData = new FormData();
    const reportExtension = reportFile.name?.includes(".")
      ? reportFile.name.slice(reportFile.name.lastIndexOf(".")).toLowerCase()
      : "";
    formData.append("orderId", reportOrderId);
    formData.append("orderMemberId", reportTarget);
    formData.append("orderItemIds", JSON.stringify(selectedItemIds));
    formData.append("report", reportFile);
    formData.append("originalFileName", reportFile.name || "report");
    formData.append("mimeType", reportFile.type || "application/octet-stream");
    formData.append("extension", reportExtension);
    if (replace) formData.append("replace", "true");
    try {
      setLoading(true);
      const response = await AuthInterceptor("/admin/order/report", {
        method: "POST",
        body: formData,
      });
      if (response?.data?.s === 409 || response?.status === 409) {
        const conflicts = Array.isArray(response?.data?.r?.conflicts) ? response.data.r.conflicts : [];
        setReportConflicts(conflicts.length ? conflicts : [{ title: "", message: response?.data?.m }]);
        return;
      }
      if ([200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status)) {
        setSnack({
          notification: {
            message: response?.data?.m || "Report uploaded successfully",
            type: "success",
          },
        });
        closeReportModal();
        fetchOrders();
      } else {
        setSnack({
          notification: { message: response?.data?.m || "Error uploading report", type: "error" },
        });
      }
    } catch (error) {
      setSnack({ notification: { message: error?.response?.data?.m || error?.message || "Error uploading report", type: "error" } });
    } finally {
      setLoading(false);
    }
  };

  const conflictTitles = reportConflicts.map((conflict) => conflict.title).filter(Boolean);
  const conflictMessage = conflictTitles.length
    ? `${conflictTitles.join(", ")} ${conflictTitles.length === 1 ? "already has" : "already have"} a report — ${
        conflictTitles.length === 1 ? "it" : "they"
      } will be moved to this new file.`
    : `${String(reportConflicts[0]?.message || "Some selected items already have a report").replace(/\.$/, "")} — they will be moved to this new file.`;

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-4">
        <ListHeader
          title="Orders"
          searchValue={query}
          searchPlaceholder="Search orders..."
          onSearchChange={setQuery}
          limit={limit}
          onLimitChange={(size) => {
            setLimit(size);
            setPageNo(1);
          }}
        />

        {/* Bulk actions (only when there are rows to select) */}
        {filteredOrders.length > 0 && (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <span className="text-muted small">{selectedIds.length} selected</span>
            <button className="btn btn-sm btn-outline-danger" onClick={() => openDeleteModal(selectedIds)}>
              Delete
            </button>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                  />
                </th>
                <th style={{ width: "60px" }}>Sr.</th>
                <th>Order Number</th>
                <th>Order Date</th>
                <th>Customer Name</th>
                <th>Mobile Number</th>
                <th>Payment Status</th>
                <th>Order Status</th>
                <th>Lab Technician Name</th>
                <th>Lab Technician Number</th>
                <th>Report Upload</th>
                <th>Download Invoice</th>
                <th style={{ width: "130px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order, index) => (
                  <tr key={order._id}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(order._id)}
                        onChange={() => toggleRowSelection(order._id)}
                      />
                    </td>
                    <td>{orderOffset + index + 1}</td>
                    <td>{order.orderNumber || formatOrderId(order, orderOffset + index)}</td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>{getCustomerName(order)}</td>
                    <td>{getCustomerMobile(order)}</td>
                    <td>
                      <span className={`badge ${statusClass(order.paymentStatus)}`}>
                        {order.paymentStatus || "Pending"}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${statusClass(order.orderStatus)}`}>
                        {order.orderStatus || "Pending"}
                      </span>
                    </td>
                    <td>{getLabTechnicianName(order)}</td>
                    <td>{getLabTechnicianNumber(order)}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2 text-nowrap">
                        {getOrderReports(order).length === 1 && (
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => openReport(getOrderReports(order)[0], setSnack)}
                          >
                            View
                          </button>
                        )}
                        {getOrderReports(order).length > 1 && (
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => openReportUpload(order)}
                          >
                            View ({getOrderReports(order).length})
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-custom"
                          onClick={() => openReportUpload(order)}
                        >
                          Upload
                        </button>
                      </div>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-custom" onClick={() => downloadInvoice(order)}>
                        Save as PDF
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => navigate(`/orders/view/${order._id}`)}
                      >
                        <CgEye size={16} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger ms-2"
                        onClick={() => openDeleteModal([order._id])}
                      >
                        <CgTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="13" className="text-center py-4 text-muted">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ListPagination
          show={(totalRecords || filteredOrders.length) > 0}
          pageNo={pageNo}
          totalPages={totalPages}
          onPageChange={setPageNo}
        />
      </div>

      <Modal show={reportModal} onHide={closeReportModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={(event) => uploadReport(event)}>
            <div className="mb-3">
              <label className="form-label">Member</label>
              {/* Bootstrap dropdown (not a native <select>). Single-member orders show it disabled. */}
              <Dropdown onSelect={(value) => selectReportMember(value)}>
                <Dropdown.Toggle
                  variant="outline-secondary"
                  className="w-100 d-flex justify-content-between align-items-center text-start"
                  disabled={!isMultiMemberOrder}
                >
                  {selectedMember ? <MemberOption member={selectedMember} /> : "Select member"}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  {reportMembers.map((member) => (
                    <Dropdown.Item
                      key={member.orderMemberId}
                      eventKey={member.orderMemberId}
                      active={reportTarget === member.orderMemberId}
                    >
                      <MemberOption member={member} />
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            {selectedMember && (
              <div className="mb-3">
                <label className="form-label">Tests / Packages in this report</label>
                {selectedMember.items.length > 0 ? (
                  <ul className="list-group">
                    {selectedMember.items.map((item) => {
                      const coveringReport = item.reportId ? findReport(item.reportId) : null;
                      const inputId = `report-item-${item.orderItemId}`;
                      return (
                        <li key={item.orderItemId} className="list-group-item">
                          <div className="form-check mb-0">
                            <input
                              id={inputId}
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedItemIds.includes(item.orderItemId)}
                              onChange={() => toggleReportItem(item.orderItemId)}
                            />
                            <label className="form-check-label d-flex flex-wrap align-items-center gap-2" htmlFor={inputId}>
                              <span>{item.title}</span>
                              <span className={`badge ${isPackageItem(item) ? "bg-info text-dark" : "bg-secondary"}`}>
                                {isPackageItem(item) ? "Package" : "Test"}
                              </span>
                            </label>
                            {item.reportId && (
                              <div className="text-muted small text-break">
                                Has report: {getReportFileName(coveringReport)}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="text-muted small">No tests or packages for this member</div>
                )}
              </div>
            )}

            <div className="mb-3">
              <label className="form-label">Report</label>
              <input
                className="form-control"
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
                onChange={(e) => {
                  setReportFile(e.target.files?.[0] || null);
                  setReportConflicts([]);
                }}
              />
              <div className="text-muted small mt-1">PDF, DOC, DOCX, JPG, or PNG</div>
            </div>

            {existingReports.length > 0 && (
              <div className="mb-3">
                <label className="form-label">Uploaded reports</label>
                <ul className="list-group list-group-flush border rounded">
                  {existingReports.map((report, index) => (
                    <li
                      key={report._id || index}
                      className="list-group-item d-flex justify-content-between align-items-center gap-2"
                    >
                      <div className="text-break">
                        <div className="fw-semibold small">{report.memberName || "Member"}</div>
                        {Array.isArray(report.itemTitles) && report.itemTitles.length > 0 && (
                          <div className="small">{report.itemTitles.join(", ")}</div>
                        )}
                        <div className="text-muted small">{getReportFileName(report)}</div>
                      </div>
                      <div className="d-flex align-items-center gap-3 flex-shrink-0">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => openReport(report, setSnack)}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-danger"
                          onClick={() => setReportToRemove(report)}
                          disabled={loading}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {reportConflicts.length > 0 ? (
              <div className="alert alert-warning mb-0" role="alert">
                <p className="mb-3">{conflictMessage}</p>
                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setReportConflicts([])}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={() => uploadReport(null, true)}
                    disabled={loading}
                  >
                    Replace
                  </button>
                </div>
              </div>
            ) : (
              <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={closeReportModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-outline-custom"
                  disabled={loading || selectedItemIds.length === 0 || !reportFile}
                >
                  Upload
                </button>
              </div>
            )}
          </form>
        </Modal.Body>
      </Modal>

      <DeleteConfirmModal
        show={Boolean(reportToRemove)}
        count={1}
        singular="report"
        plural="reports"
        loading={loading}
        onConfirm={removeUploadedReport}
        onCancel={() => setReportToRemove(null)}
      />
      <DeleteConfirmModal
        show={deleteModal}
        count={deleteIds.length}
        singular="order"
        plural="orders"
        loading={loading}
        onConfirm={deleteOrders}
        onCancel={() => {
          setDeleteModal(false);
          setDeleteIds([]);
        }}
      />
    </>
  );
};

export default OrdersList;
